import {ImageMetadata, MediaCardType} from '@wandb/weave/common/types/media';

import {RunHistoryRow} from '../../../types/run';
import {mediaFilePath} from '../../../util/media';
import {normalizeMediaFilepath} from '../../../util/requests';
import {getImageInfo} from '../../ImageCard/utils';
import {
  getAudioMedia,
  getBokehMediaPath,
  getHTMLMedia,
  getImageMedia,
  getMoleculeMediaPath,
  getObject3DMediaPath,
  getPlotlyMediaPath,
  getTableMediaPath,
  getVideoMedia,
  TileMediaSpec,
} from '../../MediaCard';

type GetFilePathFn = (p: GetFilePathParams) => string;

type GetFilePathParams = TileMediaSpec & {
  historyRow: RunHistoryRow;
};

const getFilePathFnByMediaType: Record<MediaCardType, GetFilePathFn> = {
  audio: getAudioFilePath,
  image: getImageFilePath,
  video: getVideoFilePath,
  table: getTableFilePath,
  plotly: getPlotlyFilePath,
  html: getHTMLFilePath,
  object3D: getObject3DFilePath,
  bokeh: getBokehFilePath,
  molecule: getMoleculeFilePath,
};

export function getMediaFilePath(p: GetFilePathParams): string {
  const filePath = getFilePathFnByMediaType[p.mediaType](p);
  return normalizeMediaFilepath(filePath);
}

function getAudioFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  const stepMedia = getAudioMedia({historyRow, mediaKey}, mediaIndex);
  return (
    stepMedia?.path ??
    mediaFilePath([mediaKey, historyRow._step, mediaIndex], 'audio')
  );
}

function getImageFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
  runSignature,
}: GetFilePathParams) {
  const stepMedia = getImageMedia({historyRow, mediaKey}, mediaIndex);
  if (stepMedia != null && !stepMedia.path && stepMedia.artifact_path) {
    return stepMedia.artifact_path;
  }
  const {imgFile: filePath} = getImageInfo({
    imgMedia: stepMedia as ImageMetadata | null,
    step: historyRow._step,
    mediaIndex,
    mediaKey,
    runSignature,
  });
  return filePath;
}

function getVideoFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  const stepMedia = getVideoMedia({historyRow, mediaKey}, mediaIndex);
  return (
    stepMedia?.path ??
    mediaFilePath([mediaKey, historyRow._step, mediaIndex], 'videos')
  );
}

function getTableFilePath({historyRow, mediaKey}: GetFilePathParams) {
  return getTableMediaPath({historyRow, mediaKey}) ?? '';
}

function getPlotlyFilePath({historyRow, mediaKey}: GetFilePathParams) {
  return getPlotlyMediaPath({historyRow, mediaKey}) ?? '';
}

function getHTMLFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  const stepMedia = getHTMLMedia({historyRow, mediaKey}, mediaIndex);
  return (
    stepMedia?.path ??
    mediaFilePath([mediaKey, historyRow._step, mediaIndex], 'html')
  );
}

function getObject3DFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  return getObject3DMediaPath({historyRow, mediaKey}, mediaIndex) ?? '';
}

function getBokehFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  return getBokehMediaPath({historyRow, mediaKey}, mediaIndex) ?? '';
}

function getMoleculeFilePath({
  historyRow,
  mediaKey,
  mediaIndex,
}: GetFilePathParams) {
  return getMoleculeMediaPath({historyRow, mediaKey}, mediaIndex) ?? '';
}
